<template>
  <div class="relative group hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" :class="{ 'bg-gray-100 dark:bg-gray-800': editingInformation }">
    <!-- Sticky while this section's body scrolls underneath, so it's always
         clear which section is open — stickyTop is this page's own measured
         header height, passed down from factsheet.vue. -->
    <div class="relative px-5 py-4 flex items-center gap-2 sticky z-[5]"
      :class="editingInformation ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-[#1C2128]'" :style="{ top: `${stickyTop}px` }">
      <button type="button" title="Collapse section" @click="toggleCollapsed"
        class="w-5 h-5 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer flex-shrink-0">
        <svg class="w-3.5 h-3.5 transition-transform" :class="collapsed ? '-rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
        </svg>
      </button>
      <div class="w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
        <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
        </svg>
      </div>
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Information</h2>
      <span class="text-xs font-medium text-gray-400 dark:text-gray-500">{{ informationCompletionPercent }}% complete</span>

      <!-- Hover-reveal edit affordance — members (canEdit false) never see it. -->
      <button v-if="canEdit && !editingInformation" type="button" @click="openEditInformation"
        class="absolute top-3.5 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-800">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
        </svg>
        Edit
      </button>
    </div>

    <div v-show="!collapsed" class="px-5 py-5">
      <div class="flex items-start gap-4">
        <!-- AvatarUploadCard's own template has two root nodes (its trigger
             button + a Teleport), so Vue can't auto-inherit a `class` passed
             directly to it — wrapping here is the fix, not touching the
             shared component itself, which is also used by the settings/
             account/profile pages. -->
        <div class="flex-shrink-0">
          <AvatarUploadCard
            ref="iconUploadCard"
            :picture-url="application.logo_url"
            :avatar-name="applicationInitial"
            avatar-color-class="bg-blue-600"
            :upload-url="`/api/v1/organizations/${organizationId}/org_applications/${application.id}/icon`"
            :remove-url="`/api/v1/organizations/${organizationId}/org_applications/${application.id}/icon`"
            :default-picture-url="application.default_logo_url"
            :reset-url="`/api/v1/organizations/${organizationId}/org_applications/${application.id}/icon/reset`"
            form-field-name="org_application[icon]"
            title="Change application icon"
            shape="rounded"
            size="w-14 h-14"
            avatar-text-size="text-xl"
            :locked="!editingInformation"
            locked-message="Click Edit to change the icon"
            @updated="onIconUpdated"
            @error="onIconError" />
        </div>

      <!-- Display mode: field name on the left, value on the right, so it's
           clear at a glance what each piece of information represents. -->
      <div v-if="!editingInformation" class="min-w-0 flex-1 space-y-2.5">
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Name</span>
            <span class="min-w-0 text-sm font-semibold text-gray-900 dark:text-white truncate">{{ application.name }}</span>
          </div>
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Alias</span>
            <span v-if="application.alias" class="min-w-0 text-sm text-gray-600 dark:text-gray-300 truncate">{{ application.alias }}</span>
            <span v-else class="text-sm text-gray-300 dark:text-gray-600 italic">Not specified</span>
          </div>
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Description</span>
            <div class="min-w-0 flex-1">
              <span v-if="!application.description" class="text-sm text-gray-300 dark:text-gray-600 italic">No description</span>
              <template v-else>
                <span class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {{ descriptionExpanded ? application.description : truncatedDescription }}
                </span>
                <button v-if="isDescriptionLong" type="button" @click="descriptionExpanded = !descriptionExpanded"
                  class="block text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-1 cursor-pointer">
                  {{ descriptionExpanded ? 'Show less' : 'Read more' }}
                </button>
              </template>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Source</span>
            <div class="min-w-0 flex-1">
              <a v-if="application.source && isUrl(application.source)" :href="application.source" target="_blank" rel="noopener noreferrer"
                class="flex items-center gap-1.5 min-w-0 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                <!-- White backing so a favicon with a transparent background
                     (most of them) still reads clearly against a dark card. -->
                <img v-if="faviconUrl(application.source)" :src="faviconUrl(application.source)" alt=""
                  class="w-4 h-4 rounded-sm bg-white ring-1 ring-black/10 flex-shrink-0" />
                <span class="flex-1 min-w-0 truncate">{{ application.source }}</span>
              </a>
              <span v-else-if="application.source" class="text-sm text-gray-600 dark:text-gray-300 truncate block">{{ application.source }}</span>
              <span v-else class="text-sm text-gray-300 dark:text-gray-600 italic">Not specified</span>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Version</span>
            <div v-if="application.current_version" class="min-w-0 flex items-center gap-2 flex-wrap">
              <span class="text-sm text-gray-600 dark:text-gray-300">{{ application.current_version.version }}</span>
              <span v-if="application.current_version.alias" class="text-xs text-gray-400 dark:text-gray-500 italic truncate">({{ application.current_version.alias }})</span>
              <span v-if="versionEolStatus(application.current_version) !== 'none'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0" :class="VERSION_EOL_BADGE_CLASSES[versionEolStatus(application.current_version)]">
                {{ VERSION_EOL_BADGE_LABELS[versionEolStatus(application.current_version)] }}
              </span>
            </div>
            <span v-else class="text-sm text-gray-300 dark:text-gray-600 italic">Not specified</span>
          </div>
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</span>
            <div class="min-w-0 flex items-center gap-2 flex-wrap">
              <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0" :class="statusBadgeClass(application.status)">
                <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="statusDotClass(application.status)"></span>
                {{ statusLabel(application.status) }}
              </span>
              <span v-if="displayStatusOption?.description" class="text-xs text-gray-400 dark:text-gray-500 italic truncate">({{ displayStatusOption.description }})</span>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <span class="w-24 sm:w-28 flex-shrink-0 pt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Criticality</span>
            <div class="min-w-0 flex items-center gap-2 flex-wrap">
              <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0" :class="criticalityBadgeClass(application.criticality)">
                <CriticalityBars :criticality="application.criticality" />
                {{ criticalityLabel(application.criticality) }}
              </span>
              <span v-if="displayCriticalityOption?.description" class="text-xs text-gray-400 dark:text-gray-500 italic truncate">({{ displayCriticalityOption.description }})</span>
            </div>
          </div>
      </div>

      <!-- Edit mode: form replaces the section content -->
      <div v-else class="min-w-0 flex-1 space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name<span class="text-red-500">*</span></label>
                <span class="text-[11px] tabular-nums" :class="infoName.length >= NAME_MAX_LENGTH ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'">{{ infoName.length }}/{{ NAME_MAX_LENGTH }}</span>
              </div>
              <input v-model="infoName" type="text" :maxlength="NAME_MAX_LENGTH"
                :class="informationError ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"
                class="w-full h-[42px] px-3.5 text-sm border rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">The application's name, shown across the organization.</p>
            </div>

            <StatusField v-model="infoStatus" />
            <CriticalityField v-model="infoCriticality" />
          </div>

          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Alias <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <span class="text-[11px] tabular-nums" :class="infoAlias.length >= ALIAS_MAX_LENGTH ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'">{{ infoAlias.length }}/{{ ALIAS_MAX_LENGTH }}</span>
            </div>
            <input v-model="infoAlias" type="text" :maxlength="ALIAS_MAX_LENGTH" placeholder="e.g. Copy 1"
              class="w-full h-[42px] px-3.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">An alternate display name, e.g. to tell duplicated copies apart. Doesn't change the catalog name above.</p>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <span class="text-[11px] tabular-nums" :class="infoDescription.length >= DESCRIPTION_MAX_LENGTH ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'">{{ infoDescription.length }}/{{ DESCRIPTION_MAX_LENGTH }}</span>
            </div>
            <textarea v-model="infoDescription" rows="2" placeholder="Optional" :maxlength="DESCRIPTION_MAX_LENGTH"
              class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none leading-relaxed"></textarea>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">A short summary shown on the applications list and this page.</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-[7fr_3fr] gap-3">
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Source <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <div class="relative">
                <img v-if="faviconUrl(infoSource)" :src="faviconUrl(infoSource)" alt=""
                  class="w-4 h-4 rounded-sm bg-white ring-1 ring-black/10 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input v-model="infoSource" type="text" placeholder="e.g. https://github.com/org/repo"
                  :class="faviconUrl(infoSource) ? 'pl-8' : 'pl-3.5'"
                  class="w-full h-[42px] pr-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              </div>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Repository or official source link.</p>
            </div>

            <div>
              <VersionField v-model="infoCurrentVersionId" :org-component-id="application.id" @add-new="handleAddNewVersionFromField" />
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                Version, tag, or release currently in use.
                <button type="button" @click="toggleVersionsManager" class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold cursor-pointer">
                  {{ showVersionsManager ? 'Hide versions' : 'Show versions' }}
                </button>
              </p>
            </div>
          </div>

          <!-- Versions manager: this application's own tracked catalog
               versions (org_component_versions, shared machinery with the IT
               Components page) — separate from the Version select above,
               which just picks which one is "current". -->
          <OrgComponentVersionsSection v-if="showVersionsManager" ref="versionsSectionRef"
            :organization-id="organizationId" :org-component-id="application.id"
            :global-component-id="application.global_component_id" :current-version-id="application.current_version?.id"
            :component-name="application.name" :component-logo-url="application.logo_url"
            @notify="onNotify" />

          <p v-if="informationError" class="text-xs text-red-500 dark:text-red-400 font-medium">{{ informationError }}</p>

          <div class="flex gap-2 pt-1 justify-end">
            <button type="button" @click="cancelEditInformation"
              class="px-3.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
              Cancel
            </button>
            <button type="button" @click="saveInformation" :disabled="savingInformation || !infoName.trim()"
              class="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors cursor-pointer">
              <svg v-if="savingInformation" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useOrgApplicationsStore } from 'stores/orgApplications.js'
import { useOrgComponentVersionsStore } from 'stores/orgComponentVersions.js'
import { useApplicationFactsheetSections } from 'composables/useApplicationFactsheetSections.js'
import AvatarUploadCard from 'controllers/vue_components/settings/shared/AvatarUploadCard.vue'
import OrgComponentVersionsSection from 'controllers/vue_components/organizations/it_components/OrgComponentVersionsSection.vue'
import CriticalityBars from 'controllers/vue_components/organizations/applications/CriticalityBars.vue'
import CriticalityField from 'controllers/vue_components/organizations/applications/CriticalityField.vue'
import StatusField from 'controllers/vue_components/organizations/applications/StatusField.vue'
import VersionField from 'controllers/vue_components/organizations/applications/VersionField.vue'
import { CRITICALITY_OPTIONS, criticalityLabel, criticalityBadgeClass, STATUS_OPTIONS, statusLabel, statusBadgeClass, statusDotClass } from 'controllers/vue_components/organizations/applications/applicationOptions.js'
import { versionEolStatus, VERSION_EOL_BADGE_CLASSES, VERSION_EOL_BADGE_LABELS } from 'controllers/vue_components/organizations/applications/versionEolStatus.js'

const props = defineProps({
  application: { type: Object, required: true },
  organizationId: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  stickyTop: { type: Number, default: 0 },
})

const emit = defineEmits(['updated', 'notify'])

const store = useOrgApplicationsStore()
const versionsStore = useOrgComponentVersionsStore()
const sectionPrefs = useApplicationFactsheetSections()
const SECTION_KEY = 'information'

const collapsed = computed(() => sectionPrefs.isCollapsed(SECTION_KEY))

function toggleCollapsed() {
  sectionPrefs.toggleSection(SECTION_KEY)
}

const applicationInitial = computed(() => props.application.name?.trim()?.[0]?.toUpperCase() || '?')

// Display-mode subtext for criticality — mirrors the dropdown option in edit
// mode, but driven off application.criticality directly rather than the edit
// form's local infoCriticality ref.
const displayCriticalityOption = computed(() =>
  CRITICALITY_OPTIONS.find((option) => option.value === props.application.criticality))

// Same pattern as displayCriticalityOption — driven off application.status
// directly, not the edit form's local infoStatus ref.
const displayStatusOption = computed(() =>
  STATUS_OPTIONS.find((option) => option.value === props.application.status))

const DESCRIPTION_TRUNCATE_LENGTH = 140
const descriptionExpanded = ref(false)

const isDescriptionLong = computed(() => (props.application.description || '').length > DESCRIPTION_TRUNCATE_LENGTH)

const truncatedDescription = computed(() => {
  const description = props.application.description || ''
  return isDescriptionLong.value ? `${description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trim()}...` : description
})

watch(() => props.application.id, () => {
  descriptionExpanded.value = false
})

// Source is freeform (could be "Internal" or a vendor name, not always a
// link) — only render it as a clickable link when it actually looks like one.
function isUrl(value) {
  return /^https?:\/\//i.test(value || '')
}

// A URL source gets its domain's favicon as an icon (view mode) / input
// prefix (edit mode), via Google's public favicon service — works for any
// domain, not just github.com, and Google's own fallback (a generic globe)
// covers domains with no favicon rather than us needing to handle that.
function sourceDomain(value) {
  if (!isUrl(value)) return ''
  try {
    return new URL(value).hostname
  } catch {
    return ''
  }
}

function faviconUrl(value) {
  const domain = sourceDomain(value)
  return domain ? `https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(domain)}` : ''
}

function onIconUpdated(updatedApplication) {
  emit('updated', updatedApplication)
}

function onIconError(message) {
  emit('notify', message, 'alert')
}

function onNotify(message, type) {
  emit('notify', message, type)
}

const NAME_MAX_LENGTH = 40
const DESCRIPTION_MAX_LENGTH = 150
const ALIAS_MAX_LENGTH = 100

const editingInformation = ref(false)
const savingInformation = ref(false)
const informationError = ref('')
const infoName = ref('')
const infoAlias = ref('')
const infoDescription = ref('')
const infoCriticality = ref('medium')
const infoStatus = ref('active')
const infoSource = ref('')
const infoCurrentVersionId = ref('')
const showVersionsManager = ref(false)
const versionsSectionRef = ref(null)

// Five fields make up this section (name, description, criticality, source,
// version), each worth an equal fifth — name/criticality are effectively
// always filled in this UI, so it's really description/source/version (all
// optional) that drive the percentage down from 100%.
const informationCompletionPercent = computed(() => {
  const fields = editingInformation.value
    ? [infoName.value, infoDescription.value, infoCriticality.value, infoSource.value, infoCurrentVersionId.value]
    : [props.application.name, props.application.description, props.application.criticality, props.application.source, props.application.current_version?.id]
  const filled = fields.filter((value) => value && String(value).trim().length > 0).length
  return Math.round((filled / fields.length) * 100)
})

function openEditInformation() {
  infoName.value = props.application.name
  infoAlias.value = props.application.alias || ''
  infoDescription.value = props.application.description || ''
  infoCriticality.value = props.application.criticality
  infoStatus.value = props.application.status || 'active'
  infoSource.value = props.application.source || ''
  infoCurrentVersionId.value = props.application.current_version?.id || ''
  informationError.value = ''
  editingInformation.value = true
  sectionPrefs.expandSection(SECTION_KEY)
  versionsStore.fetchIfNeeded(props.organizationId, props.application.id)
}

function cancelEditInformation() {
  editingInformation.value = false
  informationError.value = ''
  showVersionsManager.value = false
}

function toggleVersionsManager() {
  showVersionsManager.value = !showVersionsManager.value
}

// "Add a new version" footer action inside VersionField's own dropdown —
// opens the versions manager (if not already open) straight into its add
// form. nextTick since showing the manager mounts OrgComponentVersionsSection
// fresh (v-if), so its exposed openForm isn't callable until after that render.
async function handleAddNewVersionFromField() {
  showVersionsManager.value = true
  await nextTick()
  versionsSectionRef.value?.openForm()
}

function actionErrorMessage(res) {
  const validationErrors = res.error?.data?.data
  if (Array.isArray(validationErrors) && validationErrors.length) return validationErrors.join(', ')
  return 'Something went wrong. Please try again.'
}

async function saveInformation() {
  if (!infoName.value.trim()) return

  savingInformation.value = true
  informationError.value = ''

  const res = await store.updateApplication(props.application.id, {
    name: infoName.value.trim(),
    alias: infoAlias.value.trim(),
    description: infoDescription.value.trim(),
    criticality: infoCriticality.value,
    status: infoStatus.value,
    source: infoSource.value.trim(),
    org_component_version_id: infoCurrentVersionId.value,
  })

  savingInformation.value = false

  if (!res.js_error && res.response.success) {
    emit('updated', res.response.data)
    editingInformation.value = false
    emit('notify', 'Application updated successfully.', 'notice')
  } else {
    informationError.value = actionErrorMessage(res)
  }
}
</script>
